import type { OwnedAccount, SiteAccount, AccountTrialInfo } from '@hotjar/account';
import { TrialState } from '@hotjar/account';
import { daysFromNow, timeAgo } from '@hotjar/webapp/lib/time';

function daysUntilNextBillingDate(nextBillingDate: number | null): number | null {
  return nextBillingDate ? daysFromNow(nextBillingDate) : null;
}

function hasCustomTrialState(account: OwnedAccount): TrialState | null {
  const daysUntilNextBillingDateValue = daysUntilNextBillingDate(account.nextBillingDate) ?? 0;

  let state = null;

  const maybeOnTrial =
    account.hasPaidPlan &&
    account.nextBillingDate !== null &&
    !account.isCustomer &&
    account.paymentMethodType !== 'bank_transfer' &&
    account.billingCycle !== 'yearly' &&
    account.billingCycle !== 'lifetime';

  if (maybeOnTrial && daysUntilNextBillingDateValue <= 0) {
    state = TrialState.EXPIRED;
  } else if (maybeOnTrial && daysUntilNextBillingDateValue > 0) {
    state = TrialState.ACTIVE;
  }

  return state;
}

export function getTrialInfo(
  siteAccount: Omit<
    SiteAccount,
    'access' | 'adminIds' | 'emailOnFirstRecordingEnabled' | 'pictureUrl'
  >,
  ownedAccount?: OwnedAccount | undefined,
): AccountTrialInfo {
  const daysRemaining = daysUntilNextBillingDate(siteAccount.nextBillingDate);
  let { trialState } = siteAccount;

  if (
    ownedAccount &&
    (!siteAccount.trialState ||
      (siteAccount.trialState && siteAccount.trialState === 'trial_expired'))
  ) {
    // Just because an account doesn't have a trial state, doesn't
    // mean its not on an 'ad-hoc' trial where they have a customer
    // ID but no subscription and a billing date in the future.
    //
    // An account remains trial_expired when they downgrade to BASIC
    // after their trial, so we need to check if they have a paid
    // site. This will eventually be handled by trial_state.
    trialState = hasCustomTrialState(ownedAccount);
  }

  // This property returns the time remaining in a readable format. e.g. in 3 days.
  // If date is in the past, it is set to null.
  const timeUntilExpiry =
    daysRemaining && daysRemaining > 0 && siteAccount.nextBillingDate
      ? timeAgo(siteAccount.nextBillingDate)
      : null;

  // This property is created so we can easily tell when an account is
  // actually experiencing a trial, as trial_state includes expiry and ineligible states as well.
  const activeOrEligible = trialState
    ? [TrialState.ELIGIBLE, TrialState.ACTIVE].includes(trialState)
    : false;
  const active = trialState === 'trial_active';

  return {
    campaign: siteAccount.registrationCampaign,
    daysRemaining,
    state: trialState,
    timeUntilExpiry,
    activeOrEligible,
    active,
  };
}
